import { ROLE_INFO, canAccess } from '../../utils/roles';
import { Link } from 'react-router-dom';

/**
 * RoleBadge — shows the member's role as a coloured pill
 * Usage: <RoleBadge role="fully_member" />
 */
export function RoleBadge({ role, size = 'sm' }) {
  const info = ROLE_INFO[role];
  if (!info) return null;
  const sizeClass = size === 'lg' ? 'text-sm px-4 py-1.5' : 'text-xs px-3 py-1';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold ${sizeClass} ${info.color}`}>
      <span>{info.icon}</span>
      <span>{info.label}</span>
      <span className="opacity-60 font-normal arabic text-xs">{info.label_ar}</span>
    </span>
  );
}

/**
 * RoleGate — conditionally renders children based on member role
 * Shows a locked state card if the role is insufficient.
 *
 * Usage:
 *   <RoleGate member={member} accessLevel="full">
 *     <SpecialWirdContent />
 *   </RoleGate>
 */
export function RoleGate({ member, accessLevel, children, lockMessage }) {
  if (!member) return null;

  if (canAccess(member.member_role, accessLevel)) {
    return children;
  }

  const requiredRoleLabels = {
    the_lovers: 'The Lovers',
    general:    'General Member',
    full:       'Fully Member or Muqaddam',
  };

  return (
    <div className="bg-gradient-to-br from-navy-50 to-white border-2 border-dashed border-navy-200 rounded-2xl p-8 text-center">
      <div className="text-4xl mb-3">🔒</div>
      <h3 className="font-bold text-navy-700 text-lg mb-2">Members Only Content</h3>
      <p className="text-gray-500 text-sm mb-3 max-w-md mx-auto">
        {lockMessage || `This content requires the ${requiredRoleLabels[accessLevel] || accessLevel} role or higher.`}
      </p>
      <div className="bg-white rounded-xl border border-navy-100 p-4 inline-block text-left mb-4">
        <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wider">Your current role</p>
        <RoleBadge role={member.member_role} size="lg" />
      </div>
      <p className="text-gray-400 text-xs max-w-sm mx-auto">
        As you grow in your Tijaniyyah journey, your Muqaddam or admin may upgrade your role to give you access to deeper resources.
      </p>
      <Link to="/member/dashboard/profile" className="inline-block mt-4 text-xs text-cyan-600 hover:underline font-medium">
        Complete your profile →
      </Link>
    </div>
  );
}

/**
 * PendingRoleCard — shown in dashboard when role is still pending
 */
export function PendingRoleCard() {
  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="text-3xl flex-shrink-0">⏳</div>
        <div>
          <h3 className="font-bold text-amber-800 mb-1">Role Assignment Pending</h3>
          <p className="text-amber-700 text-sm leading-relaxed mb-3">
            Your email has been verified. Our admin is reviewing your registration and will assign your membership role soon, in sha Allah. You'll receive an email notification once it's done.
          </p>
          <p className="text-amber-600 text-xs font-medium">
            While waiting, you can complete your profile below to help us assign the right role for you.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * RoleProgressPath — visual journey showing all 4 roles
 * Used in dashboard to show where member is on the path
 */
export function RoleProgressPath({ currentRole }) {
  const journey = [
    { role: 'the_lovers',     step: 1 },
    { role: 'general_member', step: 2 },
    { role: 'fully_member',   step: 3 },
    { role: 'muqaddam',       step: 4 },
  ];

  const currentStep = journey.find(j => j.role === currentRole)?.step || 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Your Membership Journey</p>
      <div className="flex items-center gap-0">
        {journey.map(({ role, step }, i) => {
          const info      = ROLE_INFO[role];
          const isActive  = step === currentStep;
          const isDone    = step < currentStep;
          const isLocked  = step > currentStep;
          return (
            <div key={role} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-shrink-0">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center text-lg font-bold border-2 transition-all ${
                  isDone   ? 'bg-green-500 border-green-500 text-white' :
                  isActive ? 'border-navy-700 bg-navy-700 text-white shadow-lg scale-110' :
                             'border-gray-200 bg-gray-50 text-gray-300'
                }`}>
                  {isDone ? '✓' : info.icon}
                </div>
                <p className={`text-xs mt-1.5 font-medium text-center leading-tight max-w-[64px] ${
                  isActive ? 'text-navy-700' : isLocked ? 'text-gray-300' : 'text-gray-400'
                }`}>
                  {info.label}
                </p>
                <p className={`arabic text-xs text-center ${isActive ? 'text-navy-500' : 'text-gray-300'}`}>
                  {info.label_ar}
                </p>
              </div>
              {i < journey.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 transition-all ${step < currentStep ? 'bg-green-400' : 'bg-gray-100'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
